import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import SignaturePad from 'signature_pad';
import { IconComponent } from '../../components/icon/icon.component';

const MAX_UPLOAD_SIZE_MB = 5;

@Component({
  selector: 'app-signature-pad',
  standalone: true,
  imports: [CommonModule, TranslatePipe, IconComponent],
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.css',
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  mode: 'draw' | 'upload' = 'draw';
  uploadedPreviewUrl: string | null = null;
  uploadError = '';

  private signaturePad?: SignaturePad;
  private uploadedBlob: Blob | null = null;

  ngAfterViewInit(): void {
    setTimeout(() => this.initCanvas(), 50);
  }

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }

  private initCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    this.signaturePad = new SignaturePad(canvas, {
      minWidth: 0.5,
      maxWidth: 2.5,
      penColor: '#1c1917',
    });
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')!.scale(ratio, ratio);
    this.signaturePad.clear();
  }

  setMode(mode: 'draw' | 'upload'): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.uploadError = '';
    if (mode === 'draw') {
      setTimeout(() => this.initCanvas(), 50);
    }
  }

  get isEmpty(): boolean {
    if (this.mode === 'upload') return !this.uploadedBlob;
    return this.signaturePad?.isEmpty() ?? true;
  }

  clear(): void {
    if (this.mode === 'draw') {
      this.signaturePad?.clear();
    } else {
      this.clearUpload();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;

    this.uploadError = '';
    if (file.type !== 'image/png') {
      this.uploadError = 'signature_pad.upload_invalid_type';
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      this.uploadError = 'signature_pad.upload_too_large';
      return;
    }

    this.uploadedBlob = file;
    this.revokePreviewUrl();
    this.uploadedPreviewUrl = URL.createObjectURL(file);
  }

  clearUpload(): void {
    this.uploadedBlob = null;
    this.revokePreviewUrl();
  }

  private revokePreviewUrl(): void {
    if (this.uploadedPreviewUrl) {
      URL.revokeObjectURL(this.uploadedPreviewUrl);
      this.uploadedPreviewUrl = null;
    }
  }

  /** Ritorna il PNG pronto per l'upload, in base alla modalità attiva (disegno o file caricato). */
  getBlob(): Blob | null {
    if (this.mode === 'upload') return this.uploadedBlob;
    if (!this.signaturePad || this.signaturePad.isEmpty()) return null;
    return this.dataURLToBlob(this.signaturePad.toDataURL('image/png'));
  }

  private dataURLToBlob(dataURL: string): Blob {
    const parts = dataURL.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return new Blob([arr], { type: contentType });
  }
}
